import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import EventFinderAPI from '../services/api';
import type { AppMetadata } from '../types/api';
import { isAppMetadata } from '../utils/appMetadata';

interface AppMetadataContextValue {
  metadata: AppMetadata | null;
  isLoading: boolean;
  isUnavailable: boolean;
}

const AppMetadataContext = createContext<AppMetadataContextValue>({
  metadata: null,
  isLoading: false,
  isUnavailable: false,
});

interface AppMetadataProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export const AppMetadataProvider: React.FC<AppMetadataProviderProps> = ({
  children,
  enabled = true,
}) => {
  const [metadata, setMetadata] = useState<AppMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isUnavailable, setIsUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setMetadata(null);
      setIsLoading(false);
      setIsUnavailable(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setIsUnavailable(false);

    EventFinderAPI.getAppMetadata(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        if (isAppMetadata(response)) {
          setMetadata(response);
        } else {
          setMetadata(null);
          setIsUnavailable(true);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.name === 'AbortError') return;
        setMetadata(null);
        setIsUnavailable(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [enabled]);

  return (
    <AppMetadataContext.Provider value={{ metadata, isLoading, isUnavailable }}>
      {children}
    </AppMetadataContext.Provider>
  );
};

export function useAppMetadata(): AppMetadataContextValue {
  return useContext(AppMetadataContext);
}